export type EmailMatchFilter = {
  fromFilter?: string;
  subjectFilter?: string;
};

export type EmailMatchSample = {
  from?: string;
  subject?: string;
};

export type EmailMatchFieldResult = {
  field: "from" | "subject";
  filter: string;
  value: string;
  ok: boolean;
};

export type EmailMatchResult = {
  matches: boolean;
  fields: EmailMatchFieldResult[];
  hasConfiguredFilter: boolean;
};

function isConfigured(filter: string | undefined): filter is string {
  return typeof filter === "string" && filter.trim().length > 0;
}

function containsCI(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase());
}

export function evaluateEmailMatch(sample: EmailMatchSample, filter: EmailMatchFilter): EmailMatchResult {
  const fields: EmailMatchFieldResult[] = [];

  const fromValue = sample.from ?? "";
  const subjectValue = sample.subject ?? "";

  if (isConfigured(filter.fromFilter)) {
    fields.push({
      field: "from",
      filter: filter.fromFilter,
      value: fromValue,
      ok: containsCI(fromValue, filter.fromFilter),
    });
  }

  if (isConfigured(filter.subjectFilter)) {
    fields.push({
      field: "subject",
      filter: filter.subjectFilter,
      value: subjectValue,
      ok: containsCI(subjectValue, filter.subjectFilter),
    });
  }

  const hasConfiguredFilter = fields.length > 0;
  const matches = fields.every(f => f.ok);

  return { matches, fields, hasConfiguredFilter };
}

export type EmailMatchDefaults = {
  from?: string;
  subject?: string;
};

const TLD_RE = /\.[a-z]{2,}$/i;

function buildFromDefault(fromFilter: string): string {
  const trimmed = fromFilter.trim();
  if (trimmed.startsWith("@") && TLD_RE.test(trimmed)) {
    return `notify${trimmed}`.toLowerCase();
  }
  if (trimmed.indexOf("@") > 0 && TLD_RE.test(trimmed)) {
    return trimmed;
  }
  if (TLD_RE.test(trimmed)) {
    return `notify@${trimmed}`.toLowerCase();
  }
  return `${trimmed.toLowerCase()}@example.com`;
}

function buildSubjectDefault(subjectFilter: string): string {
  return `Your ${subjectFilter.trim()} is ready`;
}

export function buildEmailMatchDefaults(filter: EmailMatchFilter): EmailMatchDefaults {
  const defaults: EmailMatchDefaults = {};
  if (isConfigured(filter.fromFilter)) {
    defaults.from = buildFromDefault(filter.fromFilter);
  }
  if (isConfigured(filter.subjectFilter)) {
    defaults.subject = buildSubjectDefault(filter.subjectFilter);
  }
  return defaults;
}
